import React, { useState, useRef, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import {
  Box,
  TextField,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Typography,
  Button,
  Alert,
  Paper,
  Grid,
  FormGroup,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  IconButton,
  Drawer,
  Tooltip
} from '@mui/material';
import { Description, ChevronLeft, ChevronRight, Close, HelpOutline } from '@mui/icons-material';
import { fillPDFForm } from './IT2104FieldMappings';
import { Clear } from '@mui/icons-material';
import { API_URL } from '../../../utils/apiConfig';


const MIN_DRAWER_WIDTH = 400;
const MAX_DRAWER_WIDTH = 1200;
const DEFAULT_DRAWER_WIDTH = 600;

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL

const PDFViewer = ({ pdfUrl, height = '80vh' }) => (
  <Box sx={{ height, backgroundColor: 'background.paper', borderRadius: 1, overflow: 'hidden', mb: 3 }}>
    <iframe
      src={pdfUrl}
      style={{ width: '100%', height: '100%', border: 'none' }}
      title="IT-2104 Form"
    />
  </Box>
);

  const SignaturePad = ({ onSignatureChange }) => {
    const canvasRef = useRef(null);
    const signaturePadRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasSignature, setHasSignature] = useState(false);
  
    useEffect(() => {
      let mounted = true;
      const initializeSignaturePad = async () => {
        if (!canvasRef.current || !mounted) return;
  
        try {
          const SignaturePad = (await import('signature_pad')).default;
          // Re-check after the await — the guard above ran before it, and the
          // lazily-imported chunk leaves a window for a re-render to null the ref.
          if (!mounted || !canvasRef.current) return;
          const canvas = canvasRef.current;
          const container = canvas.parentElement;
          if (!container) return;

          // Set canvas dimensions
          const setCanvasDimensions = () => {
            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;
            canvas.getContext('2d').scale(ratio, ratio);
          };
  
          // Initialize dimensions
          setCanvasDimensions();
  
          // Create new signature pad instance
          if (!signaturePadRef.current) {
            signaturePadRef.current = new SignaturePad(canvas, {
              backgroundColor: 'rgb(255, 255, 255)',
              penColor: 'rgb(0, 0, 0)',
              minWidth: 1,
              maxWidth: 2.5,
              throttle: 16,
              velocityFilterWeight: 0.7,
            });
  
            // Add change listener
            signaturePadRef.current.addEventListener("endStroke", () => {
              const isEmpty = signaturePadRef.current.isEmpty();
              setHasSignature(!isEmpty);
              if (onSignatureChange) {
                onSignatureChange(!isEmpty ? signaturePadRef.current.toDataURL() : null);
              }
            });
          }
  
          setIsLoading(false);
  
          // Handle resize
          const resizeObserver = new ResizeObserver(() => {
            const data = signaturePadRef.current?.toData();
            setCanvasDimensions();
            if (data) {
              signaturePadRef.current?.fromData(data);
            }
          });
  
          if (container) {
            resizeObserver.observe(container);
          }
  
          return () => {
            resizeObserver.disconnect();
            if (signaturePadRef.current) {
              signaturePadRef.current.off();
            }
          };
  
        } catch (error) {
          console.error('Failed to initialize signature pad:', error);
          setIsLoading(false);
        }
      };
  
      initializeSignaturePad();
  
      return () => {
        mounted = false;
      };
    }, [onSignatureChange]);
  
    const clearSignature = () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
        setHasSignature(false);
        if (onSignatureChange) {
          onSignatureChange(null);
        }
      }
    };
  
    return (
      <div>
        <Paper
          variant="outlined"
          sx={{
            bgcolor: 'white',
            mb: 2,
            height: '150px',
            width: '100%',
            border: hasSignature ? '1px solid #4caf50' : '1px solid #e0e0e0'
          }}
        >
          {isLoading && (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
          <canvas
            ref={canvasRef}
            style={{
              width: '100%',
              height: '100%',
              display: isLoading ? 'none' : 'block',
              touchAction: 'none'
            }}
          />
        </Paper>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            onClick={clearSignature}
            startIcon={<Clear />}
          >
            Clear Signature
          </Button>
          {hasSignature && (
            <Typography variant="caption" color="success.main">
              Signature captured
            </Typography>
          )}
          {!hasSignature && (
            <Typography variant="caption" color="error">
              Signature required
            </Typography>
          )}
        </Box>
      </div>
    );
  };
  

const CompletedFormView = ({ 
  filePath, 
  onStartEdit,
  formTitle = "NY IT-2104 Form"
}) => {
  const API_URL = process.env.NODE_ENV === 'development' 
    ? process.env.REACT_APP_API_URL_DEVELOPMENT 
    : process.env.REACT_APP_API_URL;

  if (!filePath) {
    return (
      <Alert severity="error">
        Unable to load completed form. Please try again or contact support.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper 
        elevation={1}
        sx={{ 
          mb: 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0',
          borderRadius: 1
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e0e0e0',
            bgcolor: 'primary.light',
          }}
        >
          <Typography variant="h6" sx={{ color: 'black' }}>
            {formTitle}
          </Typography>
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={onStartEdit}
              sx={{ mr: 1 }}
            >
              Fill Form Again
            </Button>
           
          </Box>
        </Box>

        <Box sx={{ width: '100%', height: '800px', overflow: 'hidden', bgcolor: '#f5f5f5' }}>
          {filePath ? (
            <PDFViewer pdfUrl={filePath} height="100%" />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress size={40} />
              <Typography sx={{ ml: 2 }} color="textSecondary">Loading PDF...</Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

const LoadingView = () => (
  <Box 
    sx={{ 
      width: '100%', 
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: 2
    }}
  >
    <CircularProgress size={40} />
    <Typography color="textSecondary">
      Loading form...
    </Typography>
  </Box>
);
const NYIT2104Form = ({ 
  employeeId,
  formId,
  employee,
  isCompleted: initialIsCompleted,
  documentStatus: initialDocumentStatus,
  filePath: initialFilePath,
  onSubmit
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Initialize core states with props
  const [isCompleted, setIsCompleted] = useState(initialIsCompleted);
  const [isEditing, setIsEditing] = useState(false);
  const [documentStatus, setDocumentStatus] = useState(initialDocumentStatus);
  const [filePath, setFilePath] = useState(initialFilePath);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Change these from useState to useRef
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  
  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(DEFAULT_DRAWER_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [syncLoading, setSyncLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
      
    const syncFormStatus = async () => {
      if (!employeeId || !formId) {
        console.log('⚠️ Missing required IDs:', { employeeId, formId });
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }
      
      console.log('🔄 Starting form sync...', {
        employeeId,
        formId,
        initialIsCompleted,
        initialDocumentStatus
      });
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_URL}/employee-documents/sync-status/${employeeId}/${formId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
  
        if (!response.ok) {
          throw new Error('Failed to sync form status');
        }
  
        const data = await response.json();
        console.log('📥 Sync data received:', data);
        
        if (mounted) {
          // Set all state updates at once to avoid multiple re-renders
          const updates = {
            isCompleted: Boolean(data.isCompleted),
            documentStatus: data.documentStatus || initialDocumentStatus,
            filePath: data.filePath || initialFilePath,
            isInitialized: true,
            isLoading: false
          };
          
          console.log('✨ Updating state with:', updates);
          
          setIsCompleted(updates.isCompleted);
          setDocumentStatus(updates.documentStatus);
          setFilePath(updates.filePath);
          setIsInitialized(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('❌ Sync error:', error);
        if (mounted) {
          setError('Failed to load form status');
          // Even on error, we should initialize the component
          setIsInitialized(true);
          setIsLoading(false);
          
          // Fall back to initial props if sync fails
          setIsCompleted(Boolean(initialIsCompleted));
          setDocumentStatus(initialDocumentStatus);
          setFilePath(initialFilePath);
        }
      }
    };
  
    // Start sync immediately
    syncFormStatus();
    
    // Set up polling
    const intervalId = setInterval(syncFormStatus, 30000);
  
    return () => {
      console.log('🧹 Cleaning up form sync');
      mounted = false;
      clearInterval(intervalId);
    };
  }, [employeeId, formId, initialIsCompleted, initialDocumentStatus, initialFilePath]);
  
  // Add this effect to log state changes
  useEffect(() => {
    console.log('🔍 Current form state:', {
      isInitialized,
      isLoading,
      isCompleted,
      isEditing,
      hasFilePath: !!filePath,
      documentStatus
    });
  }, [isInitialized, isLoading, isCompleted, isEditing, filePath, documentStatus]);
  
  // Effect to fetch PDF when completed
  useEffect(() => {
    const fetchPDF = async () => {
      // Don't fetch if not completed or already editing
      if (!isCompleted || isEditing) {
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        
        // Use direct API URL with token - iframes can load this directly
        const url = `${API_URL}/forms/it2104-pdf/${employeeId}?token=${token}`;
        setPdfUrl(url);
      } catch (err) {
        console.error('Error fetching IT-2104 PDF:', err);
        setError(err.message || 'Failed to load IT-2104 form');
      }
    };

    fetchPDF();
  }, [employeeId, isCompleted, isEditing, filePath]);

  // Effect to initialize signature pad
  useEffect(() => {
    let mounted = true;
    let resizeObserver = null;

    const initializeSignaturePad = async () => {
      if (!canvasRef.current || !isEditing || !mounted) {
        return;
      }

      try {
        const SignaturePad = (await import('signature_pad')).default;
        // Re-check after the await: `canvas.parentElement` below throws on a
        // null ref before the existing !container guard can catch anything.
        if (!mounted || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const container = canvas.parentElement;

        if (!container) return;

        const containerWidth = container.clientWidth || 300;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);

        canvas.style.width = '100%';
        canvas.style.height = '150px';
        canvas.width = containerWidth * ratio;
        canvas.height = 150 * ratio;

        const context = canvas.getContext('2d');
        context?.scale(ratio, ratio);

        if (mounted && !signaturePadRef.current) {
          signaturePadRef.current = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5,
            throttle: 16,
          });
        }
      } catch (error) {
        console.error('Error initializing signature pad:', error);
      }
    };

    const setupResizeObserver = () => {
      if (!canvasRef.current?.parentElement) return;

      resizeObserver = new ResizeObserver(() => {
        if (signaturePadRef.current) {
          const data = signaturePadRef.current.toData();
          initializeSignaturePad().then(() => {
            if (data && signaturePadRef.current) {
              signaturePadRef.current.fromData(data);
            }
          });
        } else {
          initializeSignaturePad();
        }
      });

      resizeObserver.observe(canvasRef.current.parentElement);
    };

    if (isEditing) {
      setTimeout(() => {
        if (mounted) {
          initializeSignaturePad();
          setupResizeObserver();
        }
      }, 0);
    }

    return () => {
      mounted = false;
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    };
  }, [isEditing]);

 // Form data state
const [formData, setFormData] = useState({
  firstName: employee?.firstName || '',
  middleInitial: '',
  lastName: employee?.lastName || '',
  ssn: '',
  address: '',
  apartmentNumber: '',
  city: '',
  state: 'NY',
  zipCode: '',
  nycResident: false,
  yonkersResident: false,
  maritalStatus: 'single',
  stateAllowances: '1',
  cityAllowances: '1',
  additionalNYSAmount: '',  // <-- Added field
  additionalNYCAmount: '',  // <-- Added field
  additionalYonkersAmount: '',  // <-- Added field
  hireDate: '',
});






  // Drawer resize handlers
  const handleMouseMove = (e) => {
    if (!isResizing) return;
    const newWidth = document.body.clientWidth - e.clientX;
    if (newWidth >= MIN_DRAWER_WIDTH && newWidth <= MAX_DRAWER_WIDTH) {
      setDrawerWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDown = (e) => {
    setIsResizing(true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    e.preventDefault();
  };

  const getDrawerWidth = () => {
    if (isMobile) return '100%';
    return `${drawerWidth}px`;
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setIsCompleted(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const togglePreview = () => {
    setIsPreviewOpen(!isPreviewOpen);
  };
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Starting form submission process');
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('📝 Checking signature...');
      if (!formData.signature) {
        throw new Error('Please provide a signature');
      }
  
      console.log('✍️ Converting signature to data URL');
      const signatureImage = formData.signature;  // Use the stored signature from form state
      
      const pdfPath = '/static-forms/IT2104.pdf';
      console.log('📄 Fetching PDF template from:', pdfPath);
      
      const token = localStorage.getItem('token');
      console.log('🔑 Using token for template fetch:', !!token);
      
      const response = await fetch(pdfPath);
      
      if (!response.ok) {
        console.error('❌ Failed to fetch PDF template:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url
        });
        throw new Error(`Failed to load PDF template: ${response.statusText}`);
      }
      
      console.log('✅ PDF template fetched successfully');
      console.log('📥 Converting PDF to array buffer');
      const pdfBytes = await response.arrayBuffer();
      console.log('📄 Loading PDF document');
      const pdfDoc = await PDFDocument.load(pdfBytes);
      
      console.log('✍️ Filling PDF form with data:', {
        formDataPreview: { ...formData, ssn: '***-**-****' },
        hasSignature: !!signatureImage
      });
      const filledPdfDoc = await fillPDFForm(pdfDoc, formData, signatureImage);
      
      console.log('💾 Saving filled PDF');
      const filledPdfBytes = await filledPdfDoc.save();
      const blob = new Blob([filledPdfBytes], { type: 'application/pdf' });
      
      console.log('📦 Preparing form data for upload');
      const formDataForUpload = new FormData();
      
      // Ensure employeeId is properly passed
      if (!employeeId) {
        throw new Error('Employee ID is required');
      }
      
      // Generate filename with employee's last name
      const timestamp = Date.now();
      const safeLastName = formData.lastName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `IT2104_${safeLastName}_${employeeId}_${timestamp}.pdf`;
      
      formDataForUpload.append('file', blob, fileName);
      formDataForUpload.append('employeeId', employeeId);
      formDataForUpload.append('formId', formId);
      formDataForUpload.append('isResubmission', initialIsCompleted ? 'true' : 'false');
      
      console.log('📤 Form data prepared:', {
        fileName,
        employeeId,
        formId,
        isResubmission: initialIsCompleted
      });
      
      console.log('🚀 Uploading form to:', `${API_URL}/forms/submit-it2104`);
      console.log('🔑 Token available:', !!token);
  
    
      const uploadResponse = await fetch(`${API_URL}/forms/submit-it2104`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataForUpload
      });
  
      console.log('📡 Upload response status:', uploadResponse.status);
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('❌ Upload failed:', {
          status: uploadResponse.status,
          statusText: uploadResponse.statusText,
          error: errorText
        });
        throw new Error(`Failed to upload form: ${uploadResponse.status} ${errorText}`);
      }
  
      console.log('✅ Upload successful, parsing response');
      const result = await uploadResponse.json();
      console.log('📄 Upload result:', result);
      
      if (onSubmit) {
        console.log('🔄 Calling onSubmit callback with:', formId, result.filePath);
        onSubmit(formId, result.filePath);
      }
  
      console.log('✨ Updating component state');
      setIsCompleted(true);
      setIsEditing(false);
      // Point the preview at the streaming endpoint, not the raw stored path.
      // `result.filePath` is "uploads/it2104/…", which resolves same-origin and
      // then 302s to an R2 presigned URL — CSP frame-src has no R2 host, so the
      // iframe is blocked and the employee sees an empty box straight after a
      // successful submit. /forms/it2104-pdf streams the bytes from our origin.
      setPdfUrl(`${API_URL}/forms/it2104-pdf/${employeeId}?token=${localStorage.getItem('token')}&t=${Date.now()}`);
      console.log('🎉 Form submission completed successfully');
      
    } catch (err) {
      console.error('❌ Form submission error:', {
        error: err,
        message: err.message,
        stack: err.stack
      });
      setError(err.message);
    } finally {
      console.log('🏁 Form submission process finished');
      setIsLoading(false);
    }
  };

  if (!isInitialized || isLoading) {
    return <LoadingView />;
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ width: '100%', maxWidth: 'md', mx: 'auto', mt: 2 }}
      >
        {error}
      </Alert>
    );
  }

  if (isCompleted && !isEditing) {
    console.log('📄 Form completion state:', { 
      isCompleted, 
      isEditing, 
      filePath,
      pdfUrl,
      documentStatus
    });

    // Show loading state while waiting for file path
    if (!filePath && !pdfUrl) {
      return (
        <Box sx={{ width: '100%', textAlign: 'center', py: 3 }}>
          <CircularProgress size={40} />
          <Typography sx={{ mt: 2 }} color="textSecondary">
            Loading completed form...
          </Typography>
        </Box>
      );
    }

    return (
      <CompletedFormView
        filePath={pdfUrl || filePath}
        onStartEdit={() => {
          console.log('🖊️ Starting edit mode');
          setIsEditing(true);
          setIsCompleted(false);
        }}
        formTitle="IT-2104 Employee's Withholding Allowance Certificate"
      />
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ 
        flex: 1, 
        maxWidth: isPreviewOpen ? `calc(100% - ${drawerWidth}px)` : '100%', 
        transition: isResizing ? 'none' : 'max-width 0.3s ease',
        pr: isPreviewOpen ? `${drawerWidth}px` : 0
      }}>
           <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff5b3', borderRadius: 1, textAlign: 'left', mb:1, border: '2px solid rgb(231, 164, 164)' }}>
         
                <Typography variant="body1">
                  <a href="https://www.tax.ny.gov/forms/current-forms/it/it2104i.htm" target="_blank" rel="noopener noreferrer">Form IT-2104</a> is completed by you, as an employee, 
                  and given to your employer to instruct them how much New York State (and New York City and Yonkers) tax to withhold from your pay and send to the New York State 
                  Tax Department on your behalf.
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                Your employer will use the information you provide on this form—including residency, marital status, and allowances—to withhold these taxes from your pay. 
                The more allowances you claim, the lower the amount of tax your employer will withhold from your paycheck.
                </Typography>
                <Typography variant="body1">
                <Box>
              
                </Box>
                <Box sx={{ fontWeight: 'bold', mt: 2, fontSize: '20px' }}>
                Definition of Allowances
                  </Box>
                <Box sx={{ fontWeight: 'bold', ml: 2 }}>
                 A withholding allowance is an exemption that lowers the amount of income tax your employer must deduct from your paycheck.
                A larger number of withholding allowances means a smaller New York income tax deduction from your paycheck, and a smaller number of allowances means 
                a larger New York income tax deduction from your paycheck.
                </Box>
                </Typography>
              </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ flex: 1 }}>
            IT-2104 Employee's Withholding Allowance Certificate
          </Typography>
          <IconButton 
            onClick={togglePreview}
            sx={{ 
              position: 'fixed',
              right: isPreviewOpen ? `${drawerWidth}px` : 0,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 1,
              zIndex: 1000,
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}
          >
            {isPreviewOpen ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                1. Personal Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Middle Initial"
                    name="middleInitial"
                    value={formData.middleInitial}
                    onChange={handleInputChange}
                    inputProps={{ maxLength: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Social Security Number"
                    name="ssn"
                    value={formData.ssn}
                    onChange={handleInputChange}
                    required
                    helperText="Format: XXX-XX-XXXX"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>


          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                2. Address Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Apt Number"
                    name="apartmentNumber"
                    value={formData.apartmentNumber}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={3}>
  <TextField
    fullWidth
    label="State"
    name="state"
    value={formData.state}
    onChange={handleInputChange}
    defaultValue="NY"
    required
  />
</Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                3. Residency Status
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.nycResident}
                      onChange={handleInputChange}
                      name="nycResident"
                    />
                  }
                  label="New York City Resident"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.yonkersResident}
                      onChange={handleInputChange}
                      name="yonkersResident"
                    />
                  }
                  label="Yonkers Resident"
                />
              </FormGroup>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                4. Marital Status
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="single"
                    control={<Radio />}
                    label="Single or Head of household"
                  />
                  <FormControlLabel
                    value="married"
                    control={<Radio />}
                    label="Married"
                  />
                  <FormControlLabel
                    value="marriedWithholdHigher"
                    control={<Radio />}
                    label="Married (withhold at single rate)"
                  />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                5. Allowances
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="State Allowances"
                    name="stateAllowances"
                    type="number"
                    value={formData.stateAllowances}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="City Allowances"
                    name="cityAllowances"
                    type="number"
                    value={formData.cityAllowances}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
  
          <Card sx={{ mb: 3 }}>
  <CardContent>
    <Typography variant="h6" gutterBottom>
      6. Additional Withholding Amounts
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="New York State Additional Withholding ($)"
          name="additionalNYSAmount"
          type="number"
          value={formData.additionalNYSAmount}
          onChange={handleInputChange}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="New York City Additional Withholding ($)"
          name="additionalNYCAmount"
          type="number"
          value={formData.additionalNYCAmount}
          onChange={handleInputChange}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Yonkers Additional Withholding ($)"
          name="additionalYonkersAmount"
          type="number"
          value={formData.additionalYonkersAmount}
          onChange={handleInputChange}
        />
      </Grid>
    </Grid>
  </CardContent>
</Card>

<Card sx={{ mb: 3 }}>
  <CardContent>
    <Typography variant="h6" gutterBottom>
      Signature
      <Tooltip title="Your signature is required to complete this form">
        <IconButton size="small">
          <HelpOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SignaturePad
          onSignatureChange={(signatureData) => {
            // Store signature data in your form state
            setFormData(prev => ({
              ...prev,
              signature: signatureData
            }));
          }}
        />
      </Grid>
    </Grid>
  </CardContent>
</Card>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={isLoading}
            sx={{ mb: 4 }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Processing...
              </Box>
            ) : (
              'Submit Form'
            )}
          </Button>
        </form>
      </Box>

      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        anchor={isMobile ? "bottom" : "right"}
        open={isPreviewOpen}
        onClose={togglePreview}
        sx={{
          width: getDrawerWidth(),
          height: isMobile ? '80vh' : '100vh',
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: getDrawerWidth(),
            height: isMobile ? '80vh' : '100vh',
            boxSizing: 'border-box',
            top: isMobile ? 'auto' : 64,
          },
        }}
      >
        <Box sx={{ 
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography variant="h6">
              Form Preview
            </Typography>
            {isMobile && (
              <IconButton onClick={togglePreview} edge="end">
                <Close />
              </IconButton>
            )}
          </Box>
          
          <Box 
            sx={{ 
              flexGrow: 1,
              border: 1,
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'hidden',
              height: isMobile ? 'calc(80vh - 80px)' : 'calc(100vh - 200px)',
              userSelect: isResizing ? 'none' : 'auto',
            }}
          >
           <iframe
  src="/static-forms/IT2104.pdf"
  style={{
    width: '100%',
    height: '100%',
    border: 'none',
  }}
  title="W-4 Form Preview"
/>
          </Box>
        </Box>
        {!isMobile && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              cursor: 'col-resize',
              '&:hover': {
                backgroundColor: theme.palette.primary.main,
                opacity: 0.2,
              },
              ...(isResizing && {
                backgroundColor: theme.palette.primary.main,
                opacity: 0.2,
                width: '4px',
              }),
            }}
            onMouseDown={handleMouseDown}
          />
        )}
      </Drawer>
    </Box>
  );
};



export default NYIT2104Form;
        